import {
  Actions,
  ApiEndpoints,
  APIMethods,
  PaginationLinks,
} from "@typeDefinitions";
import { fetchData } from "@utils/api";
import { object, number, string, boolean, array } from "yup";
import { PaginationLinksSchema } from "@typeDefinitions/PaginationLinks";
import qs from "qs";
import { MetadataSchema, Metadata } from "@typeDefinitions/Metadata";

interface EducationalMaterialLinksQueryParams {
  page?: number;
  perPage?: number;
  query?: string;
  owner?: string;
  categoryId?: string;
  authorId?: string;
  reviewed?: boolean;
  favorite?: number;
  latest?: number;
}

export const fetchEducationalMaterialLinks = async (
  params?: EducationalMaterialLinksQueryParams,
): Promise<EducationalMaterialResponse> => {
  const queryParams = qs.stringify(params, {
    skipNulls: true,
    arrayFormat: "brackets",
  });

  const response = await fetchData<EducationalMaterialResponse>(
    `${ApiEndpoints.EducationalMaterialsLinksSearch}?${queryParams}`,
    APIMethods.GET,
  );

  return EducationalMaterialResponseSchema.validate(response);
};

export interface EducationalMaterialImage {
  id: number;
  url: string;
}

export const EducationalMaterialImageSchema = object({
  id: number().required(),
  url: string().required(),
});

export interface EducationalMaterialTranslation {
  lang: string;
  name: string | null;
  description: string | null;
}

export const EducationalMaterialTranslationSchema = object({
  lang: string().required(),
  name: string().nullable().defined(),
  description: string().nullable().defined(),
});

interface Category {
  id: number;
}

export interface EducationalMaterial {
  id: number;
  name: string | null;
  description: string | null;
  category: Category;
  translations: EducationalMaterialTranslation[];
  reviewed: boolean;
  image: EducationalMaterialImage | null;
  url: string | null;
  uuid?: string | null;
}

const Category = object({
  id: number().required(),
});

export const EducationalMaterialSchema = object({
  id: number().required(),
  name: string().nullable().defined(),
  description: string().nullable().defined(),
  category: Category,
  translations: array().of(EducationalMaterialTranslationSchema).required(),
  reviewed: boolean().required(),
  image: EducationalMaterialImageSchema.nullable(),
  url: string().nullable().defined(),
  uuid: string().nullable(),
});

export const ActionsSchema = object({
  canEdit: boolean().required(),
  canDelete: boolean().required(),
  canVisible: boolean().required(),
});

export interface EducationalMaterialMeta {
  current_page: number;
  from: number;
  last_page: number;
  path: string;
  per_page: number;
  to: number;
  total: number;
}

interface EducationalMaterialData {
  resource: EducationalMaterial;
  actions: Actions;
}

export const EducationalMaterialDataSchema = object({
  resource: EducationalMaterialSchema.required(),
  actions: ActionsSchema.required(),
});

export interface EducationalMaterialResponse {
  data: EducationalMaterialData[];
  links: PaginationLinks;
  meta: Metadata;
}

export const EducationalMaterialResponseSchema = object({
  data: array().of(EducationalMaterialDataSchema).required(),
  links: PaginationLinksSchema.required(),
  meta: MetadataSchema.required(),
});
